/**
 * AppShell — Pattern A layout: Sidebar (w-52) + Top Bar (48px) + Content area.
 * SINGLE layout for all 11 AxonBOS screens.
 *
 * Features:
 * - 3-section nav groups (CORE / AUTOMATION / SYSTEM)
 * - Company switcher dropdown
 * - Credits badge with color thresholds
 * - User footer with avatar + name + email + sign out
 * - Top bar with breadcrumbs and cost badge
 * - Avatar dropdown (Profile, Settings, Sign Out)
 * - Responsive: hamburger menu on <768px
 * - RBAC: nav items hidden (not greyed) based on role
 */

import * as React from 'react';
import {
  Sidebar,
  DEFAULT_NAV_GROUPS,
  type NavGroup,
  type UserRole,
  type Company,
} from './sidebar';
import { Header, type BreadcrumbItem, type UserTier } from './header';
import { cn } from '../../lib/utils';
import { colors, font } from '../../theme';

export type { NavGroup, UserRole, Company, BreadcrumbItem, UserTier };

export interface AppShellProps {
  /** Active route path (e.g., '/chat', '/data', '/dashboard') */
  activeRoute: string;
  /** Breadcrumb segments (optional, auto-generated from route if not provided) */
  breadcrumbs?: BreadcrumbItem[];
  /** Page content */
  children: React.ReactNode;
  /** Credits balance */
  creditsBalance: number;
  /** Current user tier (regular, managed, enterprise) */
  userTier?: UserTier;
  /** Current user role for RBAC visibility */
  userRole: UserRole;
  /** Company change handler */
  onCompanyChange: (companyId: string) => void;
  /** Logout handler */
  onLogout: () => void;
  /** Navigation handler */
  onNavigate: (href: string) => void;
  /** User display name */
  userName?: string;
  /** User email */
  userEmail?: string;
  /** User avatar URL */
  userAvatar?: string;
  /** List of companies for company switcher */
  companies?: Company[];
  /** Currently selected company ID */
  activeCompanyId?: string;
  /** Navigation groups (defaults to CORE/AUTOMATION/SYSTEM) */
  navGroups?: NavGroup[];
  /** Custom class for the content area */
  className?: string;
}

const DEFAULT_COMPANIES: Company[] = [
  { id: 'skm', name: 'SKM Steels' },
];

export function AppShell({
  activeRoute,
  breadcrumbs,
  children,
  creditsBalance,
  userTier = 'regular',
  userRole,
  onCompanyChange,
  onLogout,
  onNavigate,
  userName,
  userEmail,
  userAvatar,
  companies = DEFAULT_COMPANIES,
  activeCompanyId = 'skm',
  navGroups = DEFAULT_NAV_GROUPS,
  className,
}: AppShellProps) {
  const [mobileOpen, setMobileOpen] = React.useState(false);

  // Auto-generate breadcrumbs from route if none provided
  const resolvedBreadcrumbs = React.useMemo<BreadcrumbItem[]>(() => {
    if (breadcrumbs) return breadcrumbs;

    const segments = activeRoute.split('/').filter(Boolean);
    if (segments.length === 0) return [{ label: 'Home' }];

    const crumbs: BreadcrumbItem[] = [{ label: 'Home', href: '/chat' }];
    let path = '';
    for (const segment of segments) {
      path += `/${segment}`;
      const isLast = path === activeRoute;
      crumbs.push({
        label: segment.charAt(0).toUpperCase() + segment.slice(1),
        href: isLast ? undefined : path,
      });
    }
    return crumbs;
  }, [activeRoute, breadcrumbs]);

  return (
    <div className={cn('flex h-screen overflow-hidden', colors.bg.page, colors.text.body, font.family)}>
      {/* Sidebar */}
      <Sidebar
        navGroups={navGroups}
        activePath={activeRoute}
        onNavigate={onNavigate}
        userRole={userRole}
        creditsBalance={creditsBalance}
        companies={companies}
        activeCompanyId={activeCompanyId}
        onCompanyChange={onCompanyChange}
        userName={userName}
        userEmail={userEmail}
        userAvatar={userAvatar}
        onLogout={onLogout}
        mobileOpen={mobileOpen}
        onMobileToggle={() => setMobileOpen(false)}
      />

      {/* Main content area */}
      <div className="flex flex-1 flex-col overflow-hidden">
        <Header
          breadcrumbs={resolvedBreadcrumbs}
          creditsBalance={creditsBalance}
          userTier={userTier}
          userName={userName}
          userAvatar={userAvatar}
          onNavigate={onNavigate}
          onLogout={onLogout}
          onMobileSidebarToggle={() => setMobileOpen(true)}
        />

        <main className={cn('flex-1 overflow-y-auto p-6', className)}>
          {children}
        </main>
      </div>
    </div>
  );
}
